// ============================================================================
// appEmbeds.ts — РЕЕСТР EMBED (SSoT/SRP). «Приложение расширяет другое»:
// приложение регистрирует ПОСТАВЩИКА встраиваемого контента (AppEmbed):
//   - по источнику (url-паттерн, mime/файл, тип объекта);
//   - хост (чат/карточка/рабочий стол/другое приложение) спрашивает реестр
//     resolveAppEmbed(source) → { provider, meta } и рендерит лёгкий компонент.
// AppEmbed ≠ действие/expose (то открывает); embed = РЕНДЕР возможности
// приложения ВНУТРИ другого контекста. Приоритет — порядок регистрации.
// Безопасность: матчинг ТОЛЬКО на клиенте по регуляркам (никаких запросов к
// URL из сообщений — SSRF); внешние iframe — sandbox + click-to-play.
// ============================================================================

import type { ComponentType } from 'react';

export type EmbedSourceKind = 'url' | 'file' | 'object' | 'widget';

export interface EmbedSource {
  kind: EmbedSourceKind;
  /** url: полный URL (для матчинга). file: mime/расширение + fileId.
   *  object: entityType/entityCode/objectId. widget: appId. */
  url?: string;
  mime?: string;
  fileId?: string;
  entityType?: string;
  entityCode?: string;
  objectId?: string;
  appId?: string;
}

export interface EmbedMeta {
  /** url: videoId и т.п. (из extract). file: {name}. object: {title}. */
  [k: string]: unknown;
}

export interface AppEmbedContext {
  source: EmbedSource;
  meta: EmbedMeta;
  /** mode: preview (превью-карточка) | active (встроен и играет) */
  mode: 'preview' | 'active';
  /** «Открыть в приложении» (full-area/новая вкладка) — вызывает хост */
  onExpand: () => void;
  onError: (e: Error) => void;
}

export interface AppEmbedProvider {
  /** id поставщика (напр. 'youtube') */
  id: string;
  appId: string;
  name: string;
  /** матчеры источника (порядок в массиве = приоритет внутри поставщика) */
  match: Array<{
    kind: EmbedSourceKind;
    /** url: regex с захватами; file: regex mime; object: entityType+entityCode */
    pattern?: RegExp;
    entityType?: string;
    entityCode?: string;
    /** извлечь meta из совпадения (url-группы); для file/object — null */
    extract?: (m: RegExpMatchArray | null, source: EmbedSource) => EmbedMeta;
  }>;
  /** компонент встраивания (same-origin; для внешних embed используем iframe по
   *  embedUrl). Обязателен, только если нет embedUrl. */
  Embed?: ComponentType<AppEmbedContext>;
  /** URL для iframe (внешние embed); undefined = same-origin компонент */
  embedUrl?: (meta: EmbedMeta) => string;
  /** URL «открыть в приложении» (переход) */
  openUrl?: (meta: EmbedMeta) => string;
}

const providers: AppEmbedProvider[] = [];

export function registerAppEmbed(provider: AppEmbedProvider): void {
  if (providers.some((p) => p.id === provider.id)) {
    throw new Error(`app embed '${provider.id}' уже зарегистрирован`);
  }
  providers.push(provider);
}

export function listAppEmbeds(): AppEmbedProvider[] {
  return [...providers];
}

/** Найти поставщика для источника (первый подходящий по порядку регистрации). */
export function resolveAppEmbed(source: EmbedSource): { provider: AppEmbedProvider; meta: EmbedMeta } | null {
  for (const provider of providers) {
    for (const m of provider.match) {
      if (m.kind !== source.kind) continue;
      if (m.kind === 'url' && source.url && m.pattern) {
        const mm = source.url.match(m.pattern);
        if (mm) {
          const meta = m.extract ? m.extract(mm, source) : {};
          // Всегда доступен исходный URL (для browser-view/embedUrl по url)
          return { provider, meta: { url: source.url, ...meta } };
        }
      }
      if (m.kind === 'file' && source.mime && m.pattern && m.pattern.test(source.mime)) {
        return { provider, meta: m.extract ? m.extract(null, source) : {} };
      }
      if (m.kind === 'object' && source.entityType === m.entityType && source.entityCode === m.entityCode) {
        return { provider, meta: m.extract ? m.extract(null, source) : {} };
      }
      if (m.kind === 'widget' && source.appId && (!m.entityCode || m.entityCode === source.appId)) {
        return { provider, meta: m.extract ? m.extract(null, source) : {} };
      }
    }
  }
  return null;
}
